// frontend/src/components/Navbar.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, Home, Film, Calendar, User, Menu, X, LogOut, Settings, Sparkles, Search, Loader, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { getNotifications, markNotificationsRead } from "../utils/notifications";
import api from '../services/api';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const { state, logout } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  const userMenuRef = useRef<HTMLDivElement>(null);

  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  // Search users
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await api.get(`/users/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data.data || []);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (state.currentUser) {
        try {
          const data = await getNotifications();
          setNotifications(data);
          const unread = data.filter((n: any) => !n.isRead).length;
          setUnreadCount(unread);
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      }
    };
    
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [state.currentUser]);

  const handleUserClick = (username: string) => {
    navigate(`/users/${username}`);
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setIsMenuOpen(false);
  };
  const handleBellClick = async () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    if (unreadCount > 0) {
      try {
        await markNotificationsRead();
        setUnreadCount(0);
        setNotifications(notifications.map(n => ({ ...n, isRead: true })));
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }
  };

  const likedMoviesCount = state.likedMovies.length;
  const isActive = (path: string) => location.pathname === path;

  const getUserInitials = () => {
    if (!state.currentUser?.name) return 'U';
    return state.currentUser.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserAvatar = (size: 'sm' | 'lg' = 'sm') => {
    const sizeClass = size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm';
    if (state.currentUser?.avatar) {
      return (
        <img 
          src={state.currentUser.avatar} 
          alt={state.currentUser.name} 
          className={`${sizeClass} rounded-full object-cover border-2 border-white shadow-md`} 
        />
      );
    }
    return (
      <div className={`${sizeClass} bg-gradient-to-br from-romcom-pink to-romcom-red rounded-full flex items-center justify-center text-white font-bold shadow-md`}>
        {getUserInitials()}
      </div>
    );
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/movies', label: 'Movies', icon: Film },
    { to: '/seasons', label: 'Seasons', icon: Calendar },
  ];

  return (
    <>
      <nav className={`sticky top-0 z-50 bg-[#faf8f6]/95 backdrop-blur-md border-b border-romcom-pink/20 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`
}>
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">

            {/* ── LOGO ── */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="relative w-16 h-16">
  <img 
    src="/logo2.jpeg" 
    alt="RomComThis Logo" 
    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300" 
  />
  <Heart className="absolute -top-1 -left-1 w-4 h-4 text-romcom-red fill-current drop-shadow-sm" />
</div>
              <div className="leading-tight">
              <p className="text-2xl font-semibold tracking-[0.2em] text-romcom-red uppercase">RomComTHIS</p>
<h1 className="text-1xs font-playfair font-bold text-gray-800 leading-none">For the Love of RomComs</h1>
              </div>
            </Link>

            {/* ── DESKTOP NAV ── */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(({ to, label, icon: Icon }) => (
                <Link 
                  key={to} 
                  to={to}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                    isActive(to) 
                      ? 'bg-romcom-red/10 text-romcom-red' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              ))}

              <Link 
                to="/prescriptor"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  isActive('/prescriptor')
                    ? 'bg-gradient-to-r from-romcom-pink to-romcom-red text-white shadow-md'
                    : 'bg-gradient-to-r from-romcom-pink/20 to-romcom-red/20 text-romcom-red hover:from-romcom-pink hover:to-romcom-red hover:text-white hover:shadow-md'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                Romcom Prescriptor
              </Link>
            </div>

            {/* ── RIGHT SECTION ── */}
            <div className="hidden md:flex items-center gap-3">

              {/* ── USER SEARCH ── */}
              <div className="relative" ref={searchRef}>
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`p-2.5 rounded-xl transition-all ${
                    isSearchOpen 
                      ? 'bg-romcom-red/10 text-romcom-red' 
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                  title="Search users"
                >
                  <Search className="w-5 h-5" />
                </button>

                <AnimatePresence>
                  {isSearchOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search users..."
                            className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                          />
                          {isSearching && <Loader className="w-4 h-4 text-romcom-red animate-spin flex-shrink-0" />}
                        </div>
                      </div>

                      <div className="max-h-72 overflow-y-auto">
                        {searchQuery.length < 2 ? (
                          <div className="px-4 py-6 text-center">
                            <User className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">Type to search for users</p>
                          </div>
                        ) : isSearching ? (
                          <div className="px-4 py-6 text-center">
                            <Loader className="w-6 h-6 text-romcom-red animate-spin mx-auto mb-2" />
                            <p className="text-sm text-gray-400">Searching...</p>
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map(user => (
                            <button
                              key={user.id}
                              onClick={() => handleUserClick(user.username)}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                            >
                              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                                {user.avatar ? (
                                  <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-sm font-bold">
                                    {(user.name || user.username).charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-800 text-sm truncate">{user.name || user.username}</p>
                                <p className="text-xs text-gray-400 truncate">@{user.username}</p>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-gray-400">No users found for "{searchQuery}"</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── NOTIFICATIONS ── */}
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={handleBellClick}
                  className="relative p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-all"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {isNotificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-800">Notifications</h3>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.slice(0, 5).map(notification => (
                            <div 
                              key={notification.id} 
                              className={`px-4 py-3 border-b border-gray-50 ${!notification.isRead ? 'bg-romcom-red/5' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  {notification.sender?.avatar ? (
                                    <img 
                                      src={notification.sender.avatar} 
                                      alt="" 
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gradient-to-br from-romcom-pink to-romcom-red rounded-full flex items-center justify-center text-white text-xs font-bold">
                                      {notification.sender?.username?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800">
                                    <span className="font-semibold">{notification.sender?.username}</span>
                                    {' '}{notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center">
                            <Bell className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">No notifications yet</p>
                          </div>
                        )}
                      </div>
                      <div className="p-3 border-t border-gray-100">
                        <Link 
                          to="/notifications" 
                          onClick={() => setIsNotificationsOpen(false)}
                          className="block text-center text-sm text-romcom-red hover:text-romcom-pink font-medium"
                        >
                          Open notifications page
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── USER MENU ── */}
              {state.currentUser ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-3 hover:bg-gray-50 px-3 py-2 rounded-xl transition-colors"
                  >
                    {getUserAvatar()}
                    <div className="text-left">
                      <p className="font-semibold text-gray-800 text-sm truncate max-w-[120px] leading-tight">
                        {state.currentUser?.name || state.currentUser?.username}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Heart className="w-3 h-3 fill-romcom-red text-romcom-red" />
                        <span>{likedMoviesCount} favorites</span>
                      </div>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 overflow-hidden"
                      >
                        <div className="px-4 py-3 bg-gradient-to-r from-romcom-pink/10 to-romcom-blue/10 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            {getUserAvatar('lg')}
                            <div className="min-w-0">
                              <p className="font-bold text-gray-800 truncate">{state.currentUser.name}</p>
                              <p className="text-xs text-gray-500 truncate">{state.currentUser.email}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Heart className="w-3 h-3 fill-romcom-red text-romcom-red" />
                                  {likedMoviesCount}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Film className="w-3 h-3 text-blue-400" />
                                  {state.watchlist.length}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Link 
                          to="/profile" 
                          onClick={() => setIsUserMenuOpen(false)} 
                          className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          Your Profile
                        </Link>

                        <Link 
                          to="/settings" 
                          onClick={() => setIsUserMenuOpen(false)} 
                          className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                        >
                          <Settings className="w-4 h-4 text-gray-400" />
                          Settings
                        </Link>

                        <div className="border-t border-gray-100 my-1" />

                        <button 
                          onClick={handleLogout} 
                          className="flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-red-50 transition-colors text-sm"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link 
                  to="/login" 
                  className="flex items-center gap-2 bg-romcom-red text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-red-600 transition-all shadow-md hover:shadow-lg"
                >
                  <User className="w-4 h-4" />
                  Sign In
                </Link>
              )}
            </div>

            {/* ── MOBILE MENU BUTTON ── */}
            <button 
              className="md:hidden p-2 text-gray-700 hover:text-romcom-red transition-colors rounded-lg hover:bg-gray-100" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* ── MOBILE MENU ── */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-gray-100 bg-[#faf8f6] overflow-hidden"

            >
              <div className="container mx-auto px-6 py-4 space-y-1">

                {/* Mobile search */}
                <div className="relative mb-3">
                  <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                      value={searchQuery}
                      onChange={async (e) => {
                        const q = e.target.value;
                        setSearchQuery(q);
                        if (q.length >= 2) {
                          try {
                            setIsSearching(true);
                            const res = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
                            setSearchResults(res.data.data || []);
                          } catch (error) {
                            console.error('Search error:', error);
                            setSearchResults([]);
                          } finally {
                            setIsSearching(false);
                          }
                        } else {
                          setSearchResults([]);
                        }
                      }}
                    />
                    {isSearching && <Loader className="w-4 h-4 text-romcom-red animate-spin" />}
                  </div>
                  
                  {searchResults.length > 0 && searchQuery.length >= 2 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-100 shadow-lg z-20 max-h-64 overflow-y-auto">
                      {searchResults.slice(0, 5).map(user => (
                        <button 
                          key={user.id} 
                          onClick={() => { 
                            handleUserClick(user.username); 
                            setIsMenuOpen(false);
                            setSearchQuery('');
                            setSearchResults([]);
                          }} 
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(user.name || user.username).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 text-sm truncate">{user.name || user.username}</p>
                            <p className="text-xs text-gray-400 truncate">@{user.username}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile notifications summary */}
                {state.currentUser && (
                  <Link 
                    to="/notifications" 
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-3 rounded-xl text-sm text-gray-700 hover:bg-gray-50 mb-2"
                  >
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-gray-400" />
                      <span>Notifications</span>
                    </div>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                )}

                {navLinks.map(({ to, label, icon: Icon }) => (
                  <Link 
                    key={to} 
                    to={to} 
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      isActive(to) 
                        ? 'bg-romcom-red/10 text-romcom-red' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </Link>
                ))}

                <Link 
                  to="/prescriptor" 
                  onClick={() => setIsMenuOpen(false)} 
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-romcom-red bg-romcom-red/10"
                >
                  <Sparkles className="w-5 h-5" />
                  Romcom Prescriptor
                </Link>

                <div className="border-t border-gray-100 pt-3 mt-2">
                  {state.currentUser ? (
                    <>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl mb-2">
                        {getUserAvatar()}
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">{state.currentUser.name}</p>
                          <p className="text-xs text-gray-500">{likedMoviesCount} favorites</p>
                        </div>
                      </div>
                      <Link 
                        to="/profile" 
                        onClick={() => setIsMenuOpen(false)} 
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <User className="w-5 h-5 text-gray-400" />
                        Profile
                      </Link>
                      <Link 
                        to="/settings" 
                        onClick={() => setIsMenuOpen(false)} 
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Settings className="w-5 h-5 text-gray-400" />
                        Settings
                      </Link>
                      <button 
                        onClick={handleLogout} 
                        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-red-500 hover:bg-red-50"
                      >
                        <LogOut className="w-5 h-5" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link 
                      to="/login" 
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 bg-romcom-red text-white px-5 py-3 rounded-xl font-semibold text-sm justify-center"
                    >
                      <User className="w-5 h-5" />
                      Sign In
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;